/**
 * # IconButton
 *
 * A round button that only contains an icon. `aria-hidden` OR `label` is required as a prop.
 *
 * ## Props<C extends React.ElementType>
 *
 * - `as?: React.ElementType`: The HTML element or React component to render as. Default is 'button'.
 * - `aria-hidden?: boolean`: Specifies whether the button is hidden from screen readers.
 * - `color?: ButtonColor`: The color variant of the button. Default is 'secondary'.
 * - `variant?: ButtonVariant`: The visual variant of the button. Default is 'text'.
 * - `roundness?: ButtonRoundness`: The roundness of the button. Default is 'circle'.
 * - `className?: string`: Additional CSS class names for the button.
 * - `iconStyle?: IconStyle`: The style variant of the icon.
 * - `icon: IconProp`: The icon to be displayed.
 * - `iconProps?: IconProps`: Additional props to be passed to the icon component. See the Icon atom for props.
 * - `label?: string`: The label for the button (used as aria-label).
 * - `size?: 'xs' | 's' | 'm' | 'l'`: The size variant of the button. Default is 'm'.
 * - `tooltip?: string | React.ReactNode`: Tooltip content. When set, wraps the button in a WithTooltip.
 * - `tooltipProps?: WithTooltipProps`: Additional props passed to WithTooltip (e.g. placement, enabled).
 * - `children?: React.ReactNode`: The content to be rendered inside the button.
 * - `...rest: C`: Any props belonging to the component or element passed as `as`.
 *
 *
 * ## Usage
 *
 * ```tsx
 * Basic usage
 * <IconButton icon="heart" label="Like" />
 *
 * With tooltip
 * <IconButton icon="heart" label="Like" tooltip="Like this item" />
 *
 * With tooltip placement
 * <IconButton icon="heart" label="Like" tooltip="Like" tooltipProps={{ placement: 'bottom' }} />
 * ```
 */

import React, {forwardRef} from 'react';
import classnames from 'classnames';

import type {IconProp, IconStyle} from '../../atoms/Icon/Icon.react';
import Icon from '../../atoms/Icon/Icon.react';
import type {ButtonColor, ButtonRoundness, ButtonVariant} from '../../atoms/Button/Button.react';
import Button from '../../atoms/Button/Button.react';
import {WithTooltip} from '../Tooltip/Tooltip.react';
import type {WithTooltipProps} from '../Tooltip/Tooltip.react';
import './icon-button.scss';

interface Props extends PropsWithChildrenOptional, PropsWithClassNameOptional {
  as?: React.ElementType;
  'aria-hidden'?: boolean;
  color?: ButtonColor;
  variant?: ButtonVariant;
  roundness?: ButtonRoundness;
  iconStyle?: IconStyle;
  icon: IconProp;
  iconProps?: any;
  label?: string;
  size?: 'xs' | 's' | 'm' | 'l';
  /** Tooltip content. When provided, wraps the button in a WithTooltip. */
  tooltip?: string | React.ReactNode;
  /** Additional props passed to WithTooltip (e.g. placement, enabled, behavior). */
  tooltipProps?: WithTooltipProps;
}

type RequireAriaHiddenOrLabel = RequireAtLeastOne<Props, 'aria-hidden' | 'label'>;

export type IconButtonProps<C extends React.ElementType> = PolymorphicComponentPropsWithRef<
  C,
  RequireAriaHiddenOrLabel
>;

type IconButtonComponent = <C extends React.ElementType = 'button'>(
  props: IconButtonProps<C>
) => React.ReactElement | null;

export const IconButton = forwardRef(
  (
    {
      as = 'button',
      className,
      color = 'secondary',
      variant = 'text',
      icon,
      iconStyle,
      label,
      size = 'm',
      children,
      roundness = 'circle',
      iconProps,
      tooltip,
      tooltipProps,
      ...rest
    }: RequireAriaHiddenOrLabel,
    ref
  ) => {
    const button = (
      <Button
        {...rest}
        as={as}
        aria-label={label}
        className={classnames('m-icon-button', className, {
          [`m-icon-button--size-${size}`]: size
        })}
        variant={variant}
        color={color}
        roundness={roundness}
        ref={ref}
      >
        <Icon icon={icon} iconStyle={iconStyle} {...iconProps} />
        {children}
      </Button>
    );

    if (tooltip) {
      return (
        <WithTooltip content={tooltip} {...tooltipProps}>
          {button}
        </WithTooltip>
      );
    }

    return button;
  }
) as unknown as IconButtonComponent;

export default IconButton;
